import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../api/drive';
import { useAuth } from '../contexts/AuthProvider';
import { formatDate, formatSize } from '../lib/files';
import PageHeader, { PageTitle } from '../components/drive/PageHeader';

const pct = (part, whole) => (whole > 0 ? (part / whole) * 100 : 0);

/** Drive vs other apps vs free, on the server disk that holds the drive_media volume. */
const CapacityMeter = ({ storage, driveBytes }) => {
  const other = Math.max(0, storage.used - driveBytes);
  const segments = [
    { key: 'drive', label: 'LampTwist Drive', bytes: driveBytes, color: 'var(--viz-drive)' },
    { key: 'other', label: 'Other apps on the server', bytes: other, color: 'var(--viz-other)' },
  ];
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
        <div>
          <span className="text-[28px] font-semibold tracking-[-0.02em]">{formatSize(storage.free)}</span>
          <span className="ml-2 text-[14px] text-muted-foreground">free of {formatSize(storage.total)}</span>
        </div>
        <span className="text-[13.5px] text-muted-foreground">
          Drive uses {formatSize(driveBytes)} ({driveBytes && pct(driveBytes, storage.total) < 0.1 ? 'under 0.1' : pct(driveBytes, storage.total).toFixed(1)}% of the disk)
        </span>
      </div>
      <div
        role="img"
        aria-label={`Disk: ${formatSize(driveBytes)} LampTwist Drive, ${formatSize(other)} other apps, ${formatSize(storage.free)} free of ${formatSize(storage.total)}`}
        className="flex h-3 w-full overflow-hidden rounded-full bg-muted"
      >
        {segments.map((s) => (
          <span
            key={s.key}
            title={`${s.label}: ${formatSize(s.bytes)}`}
            className="h-full border-r-2 border-background last:border-r-0"
            style={{ width: `${Math.max(pct(s.bytes, storage.total), s.bytes > 0 ? 0.6 : 0)}%`, background: s.color }}
          />
        ))}
      </div>
      <ul className="flex flex-wrap gap-x-5 gap-y-1 text-[13px]">
        {segments.map((s) => (
          <li key={s.key} className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: s.color }} aria-hidden="true" />
            {s.label} <span className="text-muted-foreground">{formatSize(s.bytes)}</span>
          </li>
        ))}
        <li className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-sm bg-muted ring-1 ring-border" aria-hidden="true" />
          Free <span className="text-muted-foreground">{formatSize(storage.free)}</span>
        </li>
      </ul>
    </div>
  );
};

const Bar = ({ value, max, label }) => (
  <div className="flex items-center gap-2" title={label}>
    <div className="h-2 w-28 overflow-hidden rounded-full bg-muted" aria-hidden="true">
      <div className="h-full rounded-full" style={{ width: `${pct(value, max)}%`, background: 'var(--viz-drive)' }} />
    </div>
  </div>
);

const Th = ({ children, right }) => (
  <th scope="col" className={`whitespace-nowrap py-2.5 pr-4 font-medium ${right ? 'text-right' : ''}`}>{children}</th>
);
const Td = ({ children, right, muted }) => (
  <td className={`whitespace-nowrap py-2.5 pr-4 ${right ? 'text-right tabular-nums' : ''} ${muted ? 'text-muted-foreground' : ''}`}>{children}</td>
);
const size = (b) => (b ? formatSize(b) : '—');

const UsagePage = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    adminApi.usage().then(setData).catch(() => setError('Could not load usage.'));
  }, []);

  if (!user?.is_staff) return <p className="p-10 text-center text-muted-foreground">Only admins can see storage usage.</p>;

  const maxPerson = Math.max(1, ...(data?.people || []).map((p) => p.total));
  const maxDrive = Math.max(1, ...(data?.drives || []).map((d) => d.size + d.trash + d.versions));
  const reclaimable = data ? data.totals.trash + data.totals.versions : 0;

  return (
    <div className="flex min-h-screen flex-col">
      <PageHeader><PageTitle>Storage &amp; usage</PageTitle></PageHeader>
      <div className="flex w-full max-w-[1400px] flex-col gap-10 px-4 py-6 sm:px-6 lg:px-8">
        {error && <p className="text-destructive">{error}</p>}
        {!data && !error && <div className="h-40 animate-pulse rounded-xl bg-muted" />}
        {data && (
          <>
            <section className="flex flex-col gap-4">
              {data.storage.kind === 'volume' && data.storage.total ? (
                <CapacityMeter storage={data.storage} driveBytes={data.totals.all} />
              ) : (
                <p className="text-[14px]">Files are stored in an S3 bucket — capacity depends on your storage provider. Drive uses {formatSize(data.totals.all)}.</p>
              )}
              <dl className="grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-4">
                {[
                  ['Files', `${formatSize(data.totals.files)}`, `${data.totals.file_count.toLocaleString()} files`],
                  ['In trash', formatSize(data.totals.trash), 'Freed when deleted forever'],
                  ['Older versions', formatSize(data.totals.versions), 'Kept when files are overwritten'],
                  ['Desktop installers', formatSize(data.totals.installers), 'All published versions'],
                ].map(([k, v, hint]) => (
                  <div key={k} className="flex flex-col">
                    <dt className="text-[12.5px] text-muted-foreground">{k}</dt>
                    <dd className="text-[17px] font-semibold">{v}</dd>
                    <span className="text-xs text-muted-foreground">{hint}</span>
                  </div>
                ))}
              </dl>
              {reclaimable > 0 && (
                <p className="text-[13px] text-muted-foreground">{formatSize(reclaimable)} sits in trash and older versions — the easiest space to reclaim.</p>
              )}
              <p className="text-xs text-muted-foreground">
                Files live in the <code className="rounded bg-muted px-1">drive_media</code> volume of the Drive app on the Coolify server (nova.lamptwist.be);
                folders, permissions and history in the Drive DB database on the same server. The disk is shared with the other apps there.
              </p>
            </section>

            <section className="flex flex-col gap-2">
              <h2 className="text-base font-semibold">People</h2>
              <p className="text-[13px] text-muted-foreground">My Drive plus what each person uploaded to shared drives. Largest first.</p>
              <div className="relative overflow-x-auto">
                <table className="w-full min-w-[900px] text-left text-[13.5px]">
                  <thead>
                    <tr className="border-b border-border text-[12.5px] text-muted-foreground">
                      <Th>Person</Th><Th right>My Drive</Th><Th right>Uploads to shared drives</Th><Th right>Trash</Th>
                      <Th right>Older versions</Th><Th right>Total</Th><Th><span className="sr-only">Share of largest</span></Th><Th>Last seen</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.people.map((p) => (
                      <tr key={p.id} className={`border-b border-border/70 ${p.is_active ? '' : 'opacity-60'}`}>
                        <td className="py-2.5 pr-4">
                          <div className="flex flex-col">
                            <span className="font-medium">{p.name}</span>
                            <span className="text-xs text-muted-foreground">{p.email}{p.is_active ? '' : ' (deactivated)'}</span>
                            {p.drive_id && p.email !== user.email && (
                              <Link to={`/drive/${p.drive_id}`} className="w-fit text-xs font-medium underline underline-offset-2 hover:text-muted-foreground">
                                Open their My Drive
                              </Link>
                            )}
                          </div>
                        </td>
                        <Td right>{size(p.my_drive)}</Td>
                        <Td right>{size(p.shared_uploads)}</Td>
                        <Td right muted>{size(p.trash)}</Td>
                        <Td right muted>{size(p.versions)}</Td>
                        <Td right><b>{size(p.total)}</b></Td>
                        <td className="py-2.5 pr-4"><Bar value={p.total} max={maxPerson} label={`${p.name}: ${formatSize(p.total)}`} /></td>
                        <Td muted>{p.last_seen ? formatDate(p.last_seen) : 'Never'}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section className="flex flex-col gap-2">
              <h2 className="text-base font-semibold">Shared drives</h2>
              <div className="relative overflow-x-auto">
                <table className="w-full min-w-[700px] text-left text-[13.5px]">
                  <thead>
                    <tr className="border-b border-border text-[12.5px] text-muted-foreground">
                      <Th>Drive</Th><Th right>Files</Th><Th right>Size</Th><Th right>Trash</Th><Th right>Older versions</Th><Th><span className="sr-only">Share of largest</span></Th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.drives.length === 0 && <tr><td colSpan={6} className="py-4 text-muted-foreground">No shared drives yet.</td></tr>}
                    {data.drives.map((d) => (
                      <tr key={d.id} className="border-b border-border/70">
                        <td className="py-2.5 pr-4">
                          <span className="flex items-center gap-2 font-medium">
                            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: d.color }} aria-hidden="true" />{d.name}
                          </span>
                        </td>
                        <Td right muted>{d.files.toLocaleString()}</Td>
                        <Td right><b>{size(d.size)}</b></Td>
                        <Td right muted>{size(d.trash)}</Td>
                        <Td right muted>{size(d.versions)}</Td>
                        <td className="py-2.5 pr-4"><Bar value={d.size + d.trash + d.versions} max={maxDrive} label={`${d.name}: ${formatSize(d.size + d.trash + d.versions)}`} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default UsagePage;
